import { VisualDesignerDto } from '@/api-sdk';
import { FgConfigurator } from '@/components';
import { Mode } from '@/types';
import { createContext } from '@lit/context';
import { LitElement } from 'lit';

export interface ConfiguratorContextType {
  /** The product identifier. */
  product: string;
  /** The color code of the product (optional). For example: 006, 007, 024, 005 or 002 etc. This property is set in CE-FE. */
  color?: string;
  /** The configuration ID (optional). */
  configurationId?: string;
  /** Indicates if the configuration is being edited. */
  editing: boolean;
  /** The mode of the configuration. */
  mode: Mode;
  /** The URL of the product image (optional). */
  productImage?: string;
  /** The name of the product (optional). */
  productName?: string;
  /** Indicates if the product is salable. */
  salable: boolean;
  /** The URL of the shop (optional). */
  shopUrl?: string;
  /** Unique selling points of the product (optional). */
  usps?: { text: string }[];
  /** The height of the sticky summary (optional). */
  stickySummaryHeight?: number;
  /** Indicates if the steps are valid. */
  hasValidSteps: boolean;
  /** Indicates if HSP is enabled (optional). */
  hspEnabled?: boolean;
  /** Indicates if the color step is collapsible (optional). */
  colorCollapsible?: number;
  /** Indicates if the print option step is collapsible (optional). */
  printOptionCollapsible?: number;
  /** Indicates if the quantity step is collapsible (optional). */
  quantityCollapsible?: number;
  /** Indicates if mobile is collapsible (optional). */
  mobileCollapsible?: string;
  /** Indicates if the production speed step needs to be rendered as step or part of the summary (optional) */
  showProductionSpeedStep?: number;
  /** Selected print option codes */
  selectedPrintOptions?: { code: string }[];
  /** Indicates if the visual designer is available */
  visualDesigner?: VisualDesignerDto;
  /** Indicates if the subtotal zero price should be hidden for A/B testing */
  hideSubtotalZeroPrice: boolean;
  /** Indicates if the first getConfigurator API call has completed (used to distinguish initial load from subsequent updates) */
  firstGetConfiguratorCall?: boolean;
}

export const configuratorGlobal = {
  context: createContext<ConfiguratorContextType>(
    Symbol('ConfiguratorContext'),
  ),
  resetContext: createContext<boolean>('ResetConfigurator'),
  /**
   *
   * @param component
   * @returns {ConfiguratorContextType}
   */
  init(component: FgConfigurator): ConfiguratorContextType {
    return {
      product: component.product,
      color: component.color,
      configurationId: component.configurationId,
      editing: component.editing,
      mode: component.mode,
      productImage: component.productImage,
      productName: component.productName,
      salable: component.salable,
      shopUrl: component.shopUrl,
      usps: component.usps,
      hasValidSteps: Object.values(component.validSteps).every(Boolean),
      stickySummaryHeight: component.stickySummaryHeight || 0,
      hspEnabled: false,
      colorCollapsible: component.colorCollapsible,
      printOptionCollapsible: component.printOptionCollapsible,
      quantityCollapsible: component.quantityCollapsible,
      mobileCollapsible: component.mobileCollapsible,
      showProductionSpeedStep: component.showProductionSpeedStep,
      selectedPrintOptions: component.selectedPrintOptions,
      visualDesigner: component.visualDesigner,
      hideSubtotalZeroPrice: component.hideSubtotalZeroPrice,
      firstGetConfiguratorCall: false,
    };
  },
  updateGlobalProviderEvent(
    element: LitElement,
    data: ConfiguratorContextType,
  ) {
    element.dispatchEvent(
      new CustomEvent<{ context: ConfiguratorContextType }>(
        'configurator-context-update',
        {
          composed: true,
          detail: {
            context: data,
          },
        },
      ),
    );
  },
  updateResetProviderUpdate(element: LitElement, reset: boolean) {
    element.dispatchEvent(
      new CustomEvent<boolean>('configurator-reset-update', {
        composed: true,
        detail: reset,
      }),
    );
  },
};
